import { Box, Modal, TextField, Typography, Button, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import UserModal from '../../../../layouts/UserModal';
import CalendarIcon from '../../../CalendarIcon';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { errorMessages, validateDateRange } from '../../../../utils/methods';
import { updateLockerPassDate, updateProductPassDate } from '../../../../api/user';
import { applyLockerPassPlan } from '../../../../api/member-management';
import PeriodChangePreviewModal from '../../../common/modal/PeriodChangePreviewModal';
import { buildLockerShiftPlan } from '../../../../utils/lockerShiftPlan';
import moment from 'moment';

/**
 * @param {Array} spotPasses 같은 라커 자리의 다른 이용권들 (라커일 때만).
 *   기간이 뒤 이용권과 겹치면 "뒤 이용권을 함께 밀지" 물어보고, 승낙하면 배치로 한 번에 저장한다.
 */
export default function ChangeGeneralProductPeriodModal({
  open,
  onClose,
  ticket,
  memberId,
  getAllUserTickets,
  spotPasses = [],
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const bigUser = useSelector(state => state.user.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 뒤 이용권을 밀어야 할 때의 확인 대상 — { entries, shiftedCount }
  const [preview, setPreview] = useState(null);

  // 락커인지 일반상품인지 구분
  const isLocker = !!ticket?.memberLockerPassId;
  const passId = isLocker ? ticket?.memberLockerPassId : ticket?.memberProductPassId;

  const { handleSubmit, control, watch, setValue, reset } = useForm({
    defaultValues: {
      startDate: ticket?.startDate ? new Date(ticket.startDate) : null,
      expireDate: ticket?.expireDate ? new Date(ticket.expireDate) : null,
    },
  });

  // ticket이 변경되면 form 값 업데이트
  useEffect(() => {
    if (ticket) {
      reset({
        startDate: ticket?.startDate ? new Date(ticket.startDate) : null,
        expireDate: ticket?.expireDate ? new Date(ticket.expireDate) : null,
      });
    }
  }, [ticket, reset]);

  const handleClose = () => {
    onClose();
  };

  const submit = async values => {
    const { expireDate, startDate } = values;

    if (!startDate) {
      toast.error(t('시작일자를 입력해주세요'));
      return;
    }

    if (!expireDate) {
      toast.error(t('종료일자를 입력해주세요'));
      return;
    }

    if (startDate > expireDate) {
      toast.error(t('기간을 다시 설정해주세요'));
      return;
    }

    // 날짜 유효성 검증
    if (!validateDateRange(startDate, expireDate)) {
      toast.error(t('기간을 다시 확인해주세요'));
      return;
    }

    // 라커는 같은 자리 뒤 이용권과 겹치면 그대로 저장할 수 없다.
    // 뒤 이용권을 이용 일수만큼 밀어내는 계획을 만들어 확인을 받는다.
    if (isLocker && ticket?.branchLockerId) {
      const plan = buildLockerShiftPlan({ spotPasses, passId, startDate, expireDate });
      if (plan?.blockedBy) {
        toast.error(
          plan.blockedKind === 'stopped'
            ? t('중지 중인 이용권 기간({{period}})과 겹칩니다', { period: plan.blockedBy })
            : t('이미 지난 이용 기록({{period}})과 겹칩니다', { period: plan.blockedBy })
        );
        return;
      }
      if (plan && plan.shiftedCount > 0) {
        setPreview({ startDate, expireDate, plan });
        return;
      }
    }

    // 기간이 바뀌면 상태(이용중/사용예정/만료)도 함께 뒤집힌다. 저장 전에 무엇이 어떻게 바뀌는지 보여준다.
    setPreview({ startDate, expireDate, plan: null });
  };

  const applyPeriodChange = async () => {
    if (!preview) return;
    const { startDate, expireDate, plan } = preview;
    try {
      setIsSubmitting(true);

      if (plan) {
        await applyLockerPassPlan(bigUser.token, memberId, ticket.branchLockerId, plan.entries);
      } else {
        const data = {
          startDate: moment(startDate).format('YYYY-MM-DD'),
          expireDate: moment(expireDate).format('YYYY-MM-DD'),
        };
        if (isLocker) {
          await updateLockerPassDate(memberId, passId, data, bigUser.token);
        } else {
          await updateProductPassDate(memberId, passId, data, bigUser.token);
        }
      }

      toast.success(t('변경이 완료되었습니다'));
      setPreview(null);
      setIsSubmitting(false);
      getAllUserTickets();
      handleClose();
    } catch (error) {
      setIsSubmitting(false);
      toast.error(errorMessages(error));
    }
  };



  // 시작일만 오늘로 옮기고 이용 일수는 그대로 유지한다.
  // 종료일을 고정하면 산 기간이 조용히 줄어드니 같은 길이만큼 뒤로 민다.
  const handleStartToday = () => {
    const start = watch('startDate');
    const expire = watch('expireDate');
    const today = moment().startOf('day');
    if (start && expire) {
      const span = moment(expire).startOf('day').diff(moment(start).startOf('day'), 'days');
      setValue('startDate', today.toDate());
      setValue('expireDate', today.clone().add(span, 'days').toDate());
      return;
    }
    setValue('startDate', today.toDate());
  };

  const handlePeriodChange = () => {
    const currentStartDate = watch('startDate');
    if (!currentStartDate) {
      toast.error(t('시작일자를 먼저 입력해주세요'));
      return;
    }

    // 상품 마스터의 이용기간(periodValue/periodType)을 우선 쓴다. 상품명에 기간이 안 적힌
    // 상품도 계산되고, 단위가 '일'인 상품이 '개월'로 잘못 잡히지 않는다.
    const periodValue = Number(ticket?.periodValue) || 0;
    if (periodValue > 0) {
      const unit = ticket?.periodType === 'DAY' ? 'days' : 'months';
      setValue('expireDate', moment(currentStartDate).add(periodValue, unit).subtract(1, 'day').toDate());
      return;
    }

    // 폴백 — 기간 값을 내려주지 않는 구버전 백엔드에서는 상품명에서 추출한다 (예: "락커 1개월 이용권").
    const productName = ticket?.productName || '';
    const monthMatch = productName.match(/(\d+)개월/);
    const dayMatch = productName.match(/(\d+)일/);

    if (monthMatch) {
      const months = parseInt(monthMatch[1], 10);
      setValue('expireDate', moment(currentStartDate).add(months, 'months').subtract(1, 'day').toDate());
    } else if (dayMatch) {
      const days = parseInt(dayMatch[1], 10);
      setValue('expireDate', moment(currentStartDate).add(days, 'days').subtract(1, 'day').toDate());
    } else {
      toast.warning(t('상품명에서 기간 정보를 찾을 수 없습니다'));
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="change-period-modal-title"
        aria-describedby="change-period-modal-description">
      <UserModal
        title={t('기간 변경')}
        handleCloseModal={handleClose}
        handleSubmit={handleSubmit(submit)}
        isSubmitting={isSubmitting}>
        {/* 상품 정보 */}
        <Box sx={{ display: 'flex', alignItems: 'center', pb: '20px' }}>
          <Typography sx={{ minWidth: '120px' }}>{t('상품명')}</Typography>
          <Typography sx={{ fontWeight: 500 }}>
            {ticket?.productName}
            {ticket?.lockerNumber && ` (${ticket.lockerNumber}번 라커)`}
          </Typography>
        </Box>

        {/* 기간 설정 */}
        <Box
          className="form1"
          sx={{
            display: 'flex',
            alignItems: 'center',
            pb: '30px',
          }}>
          <Typography sx={{ minWidth: '120px' }}>{t('기간')}</Typography>
          <FormInputDate
            name="startDate"
            control={control}
            placeholder={t('DD/MM/YYYY')}
            components={{
              OpenPickerIcon: CalendarIcon,
            }}
            renderInput={params => (
              <TextField
                sx={{
                  '& .MuiSvgIcon-root': {
                    fill: '#0F7F85 !important',
                  },
                }}
                size="small"
                {...params}
              />
            )}
          />
          <Typography sx={{ mx: 1 }}> ~ </Typography>
          <FormInputDate
            name="expireDate"
            control={control}
            placeholder={t('DD/MM/YYYY')}
            minDate={watch('startDate') || null}
            disabled={!watch('startDate')}
            components={{
              OpenPickerIcon: CalendarIcon,
            }}
            renderInput={params => (
              <TextField
                sx={{
                  '& .MuiSvgIcon-root': {
                    fill: '#0F7F85 !important',
                  },
                }}
                size="small"
                {...params}
              />
            )}
          />
        </Box>

        {/* 매장  */}

        {/* 시작일을 오늘로 */}
        <Box className="form1" sx={{ display: 'flex', alignItems: 'center', pb: 2 }}>
          <Box sx={{ minWidth: '120px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography>{t('시작일 오늘로')}</Typography>
            <Tooltip
              title={t('시작일을 오늘로 옮기고 이용 일수는 그대로 유지합니다')}
              arrow
              placement="top">
              <HelpOutlineIcon sx={{ fontSize: 16, color: '#888', cursor: 'pointer' }} />
            </Tooltip>
          </Box>
          <Button
            onClick={handleStartToday}
            sx={{
              background: '#fff',
              border: '1px solid #0F7F85',
              color: '#0F7F85',
              borderRadius: '8px',
              '&:hover': {
                background: '#0F7F85',
                color: '#fff',
              },
            }}>
            {t('오늘로 변경')}
          </Button>
        </Box>

        {/* 기간 변경 적용 버튼 */}
        <Box className="form1" sx={{ display: 'flex', alignItems: 'center', pb: 2 }}>
          <Box sx={{ minWidth: '120px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography>{t('기간 변경 적용')}</Typography>
            <Tooltip title={t('시작일 기준으로 상품의 기간을 다시 계산합니다')} arrow placement="top">
              <HelpOutlineIcon sx={{ fontSize: 16, color: '#888', cursor: 'pointer' }} />
            </Tooltip>
          </Box>
          <Button
            onClick={handlePeriodChange}
            sx={{
              background: '#0F7F85',
              border: '1px solid #0F7F85',
              color: '#fff',
              borderRadius: '8px',
              '&:hover': {
                background: 'none',
                color: '#0F7F85',
              },
            }}>
            {t('실행하기')}
          </Button>
        </Box>
      </UserModal>
      </Modal>

      {/* 뒤 이용권을 밀어야 할 때 — 무엇이 어떻게 밀리는지 보여주고 확인받는다 */}
      <PeriodChangePreviewModal
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        onConfirm={applyPeriodChange}
        isSubmitting={isSubmitting}
        productName={ticket?.productName}
        before={{ startDate: ticket?.startDate, expireDate: ticket?.expireDate, status: ticket?.status }}
        after={{ startDate: preview?.startDate, expireDate: preview?.expireDate, status: ticket?.status }}
        shifted={preview?.plan?.shifted ?? []}
      />
    </>
  );
}
